// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import {
  Form, TextArea, Select, Option,
} from 'informed';
import moment from 'moment';

import HistoryTable from './HistoryTable.jsx';
import DateText from '../DateText.jsx';
import { Label, HeaderText, PrimaryText } from '../Typography.jsx';
import { Button, PrimaryButton } from '../Buttons.jsx';
import Typeahead from '../Typeahead.jsx';

import styles from '../../styles/DrugDispenser.scss';

type Props = {
  drug: Object,
  drugList: Object,
  // if view only, no fields editable
  viewOnly: bool,
  /*
    (rowId: string, row: Object) => {}
  */
  handleSaveRow: Function,
  /*
    (row: Object) => {}
  */
  handleSaveNewRow: Function,
  closeModal: Function,
  fetchDrugWithId: Function,
}

type State = {
  editing: bool,
}

class DrugDispenserComponent extends React.Component<Props, State> {
  formApi: {
    getState: Function,
    setValues: Function,
    setValue: Function,
    reset: Function,
    getTouched: Function,
  }

  constructor(props: Props) {
    super(props);
    this.state = {
      editing: false,
    };
  }

  setFormApi = (formApi: Object) => {
    this.formApi = formApi;
  }

  handleCancel = () => {
    this.props.closeModal();
    this.setState({
      editing: false,
    });
  }

  onCancel = () => {
    this.setState({
      editing: false,
    });
  }

  onSave = () => {
    const state = this.formApi.getState();
    if (state.invalid) {
      return;
    }
    if (!Object.keys(this.props.drug).length) {
      this.props.handleSaveNewRow(state);
    } else if (Object.keys(state.touched).length) {
      this.props.handleSaveRow(this.props.drug._id, state);
    }
    if (this.formApi.getTouched('category')) {
      this.props.closeModal();
    }
    this.setState({
      editing: false,
    });
  }

  onEdit = () => {
    this.setState({
      editing: true,
    });
  }

  render() {
    const { viewOnly, drugList } = this.props;
    let { drug } = this.props;

    if (drug._id) {
      drug = drugList[drug.category].find(x => x._id === drug._id);
    }

    const {
      genericName,
      proprietaryName,
      category,
      classification,
      type,
      description,
      acquiredDate,
      manufactureDate,
      expirationDate,
      lotNumber,
      concentration,
      amountPerUnit,
      numberRemainingUnits,
      notes,
      editors,
      editDates,
    } = drug;

    let genericNameContent = genericName;
    let proprietaryNameContent = proprietaryName;
    let categoryContent = category;
    let classificationContent = classification;
    let typeContent = type;
    let descriptionContent = description;
    const acquiredDateContent = acquiredDate ? moment(acquiredDate).format('DD-MM-YYYY, HH:mm') : acquiredDate;
    let manufactureDateContent = manufactureDate ? moment(manufactureDate).format() : manufactureDate;
    let expirationDateContent = expirationDate ? moment(expirationDate).format() : expirationDate;
    let lotNumberContent = lotNumber;
    let concentrationContent = concentration;
    let amountPerUnitContent = amountPerUnit;
    const numberRemainingUnitsContent = numberRemainingUnits;
    let notesContent = notes;

    if (this.state.editing && !viewOnly) {
      genericNameContent = <Typeahead field='genericName' initialValue={genericName} endpoint='genericName' />;
      proprietaryNameContent = <Typeahead field='proprietaryName' initialValue={proprietaryName} endpoint='propName' />;
      categoryContent = (
        <Select field='category' initialValue={category}>
          <Option value='Allergy Medicine'>Allergy Medicine</Option>
          <Option value='Antibiotics'>Antibiotics</Option>
          <Option value='Anti-epileptics'>Anti-epileptics</Option>
          <Option value='Antifungals'>Antifungals</Option>
          <Option value='Antivirals'>Antivirals</Option>
          <Option value='Respiratory Meds'>Respiratory Meds</Option>
          <Option value='Decongestants'>Decongestants</Option>
          <Option value='Steroids'>Steroids</Option>
          <Option value='Supplements'>Supplements</Option>
          <Option value='Immunizations'>Immunizations</Option>
          <Option value='Analgesics'>Analgesics</Option>
          <Option value='ADHD Meds'>ADHD Meds</Option>
          <Option value='GI Meds'>GI Meds</Option>
          <Option value='Muscle Relaxants'>Muscle Relaxants</Option>
          <Option value='Other'>Other</Option>
        </Select>
      );
      classificationContent = <Typeahead field='classification' initialValue={classification} endpoint='drugClass' />;
      typeContent = <Typeahead field='type' initialValue={type} endpoint='drugType' placeholder='e.g. tablets, syrup' />;
      descriptionContent = <Typeahead field='description' initialValue={description} endpoint='drugDescription' placeholder='e.g. small white tablet' />;
      manufactureDateContent = <DateText field='manufactureDate' item={{ manufactureDate }} initEmpty />;
      expirationDateContent = <DateText field='expirationDate' item={{ expirationDate }} initEmpty />;
      lotNumberContent = <Typeahead field='lotNumber' initialValue={lotNumber} endpoint='lotNum' />;
      concentrationContent = <Typeahead field='concentration' initialValue={concentration} endpoint='drugConcentration' placeholder='e.g. 5 mg/mL' />;
      amountPerUnitContent = <Typeahead field='amountPerUnit' initialValue={amountPerUnit} endpoint='drugUnitAmt' placeholder='e.g. 10 mL' />;
      notesContent = <TextArea field='notes' initialValue={notes} />;
    }

    let editHistory = '';
    if (editors) {
      for (let i = 0; i < editors.length; i++) {
        if (i === editors.length - 1) {
          editHistory += `${moment(editDates[i]).format('DD-MM-YYYY, HH:mm')} - ${editors[i]} (created) \n`;
        } else {
          editHistory += `${moment(editDates[i]).format('DD-MM-YYYY, HH:mm')} - ${editors[i]}\n`;
        }
      }
    }

    return (
      <div>
        <Form getApi={this.setFormApi} autoComplete='off'>
          <HeaderText>Dispensary</HeaderText>
          <div className={styles.drugContainer}>
            <PrimaryText>{genericName || 'Add a drug'}</PrimaryText>
            <div className={styles.drugInfoContainer}>
              <table>
                <tbody>
                  <tr>
                    <td><Label>Category:</Label></td>
                    <td>{categoryContent || '--'}</td>
                  </tr>
                  <tr>
                    <td><Label>Generic drug name:</Label></td>
                    <td>{genericNameContent || '--'}</td>
                  </tr>
                  <tr>
                    <td><Label>Proprietary drug name:</Label></td>
                    <td>{proprietaryNameContent || '--'}</td>
                  </tr>
                  <tr>
                    <td><Label>Drug class:</Label></td>
                    <td>{classificationContent || '--'}</td>
                  </tr>
                  <tr>
                    <td><Label>Type of drug:</Label></td>
                    <td>{typeContent || '--'}</td>
                  </tr>
                  <tr>
                    <td><Label>Description:</Label></td>
                    <td>{descriptionContent || '--'}</td>
                  </tr>
                </tbody>
              </table>

              <table>
                <tbody>
                  <tr>
                    <td><Label>Acquired date:</Label></td>
                    <td>{acquiredDateContent || '--'}</td>
                  </tr>
                  <tr>
                    <td><Label>Manufacture date:</Label></td>
                    <td>{manufactureDateContent || '--'}</td>
                  </tr>
                  <tr>
                    <td><Label>Expiration date:</Label></td>
                    <td>{expirationDateContent || '--'}</td>
                  </tr>
                  <tr>
                    <td><Label>Lot number:</Label></td>
                    <td>{lotNumberContent || '--'}</td>
                  </tr>
                  <tr>
                    <td><Label>Concentration:</Label></td>
                    <td>{concentrationContent || '--'}</td>
                  </tr>
                  <tr>
                    <td><Label>Amount per unit:</Label></td>
                    <td>{amountPerUnitContent || '--'}</td>
                  </tr>
                  <tr>
                    <td><Label>Number of units remaining</Label></td>
                    <td>{numberRemainingUnitsContent === 0 ? 0 : numberRemainingUnitsContent || '--'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div>
              <Label>Notes:</Label>
              <div>{notesContent || '--'}</div>
            </div>
            <br />
            <div>
              <Label>Edit History:</Label>
              <div>{editHistory || '--'}</div>
            </div>
            {!viewOnly && (
              this.state.editing ? (
                <div>
                  <PrimaryButton onClick={this.onSave}>Save</PrimaryButton>
                  <Button onClick={this.onCancel}>Cancel</Button>
                </div>
              ) : (
                <div>
                  <PrimaryButton onClick={this.onEdit}>Edit</PrimaryButton>
                </div>
              )
            )}
          </div>
        </Form>
        <PrimaryText>Dispensary History</PrimaryText>
        <br />
        <HistoryTable drug={drug} viewOnly={viewOnly} />
        <Button onClick={this.handleCancel} style="position: absolute; right: 0;">Close</Button>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  drugList: state.pharmacy.data.drugList,
});

const DrugDispenser = connect(
  mapStateToProps,
)(DrugDispenserComponent);

export default DrugDispenser;



// WEBPACK FOOTER //
// ./client/src/components/pharmacyPages/DrugDispenser.jsx